import { Video, HlsVideo } from "@types";
import Hls from "hls.js";
// import debugService from "@api/debugService";

/**
 * Утилита для генерации URL-адресов миниатюр видео
 */
export class ThumbnailGenerator {
  // Кэш для хранения созданных миниатюр
  private static frameCache: Map<string, string> = new Map();

  // Флаги для отслеживания попыток извлечения
  private static extractionAttempts: Map<string, boolean> = new Map();

  // HLS объект для работы с потоковыми видео
  private static hlsInstance: Hls | null = null;

  /**
   * Определяет, имеет ли видео вертикальную ориентацию
   * @param video Объект видео
   * @returns true, если видео вертикальное
   */
  static isVerticalVideo(video: Video | HlsVideo): boolean {
    if (video.width && video.height) {
      // Считаем вертикальным, если соотношение ширины к высоте < 0.75
      return video.width / video.height < 0.75;
    }
    return false;
  }

  /**
   * Получает URL миниатюры для видео
   * @param video Объект видео
   * @returns URL миниатюры
   */
  static getVideoThumbnailUrl(video: Video | HlsVideo): string {
    // Логирование для отладки
    // debugService.info("Запрос миниатюры для видео", {
    //   url: video.url,
    // });

    // Приоритет:
    // 1. Явная миниатюра
    // 2. Превью
    // 3. Короткое превью
    // 4. Кадр из кэша
    // 5. Запрос на извлечение кадра, если еще не пытались
    // 6. Запасное изображение
    if (video.thumbnailUrl) {
      return video.thumbnailUrl;
    }

    if (video.preview_url) {
      return video.preview_url;
    }

    if (video.short_video_preview_url) {
      return video.short_video_preview_url;
    }

    // Проверяем наличие в кэше
    if (this.frameCache.has(video.url)) {
      // debugService.info("Использую кэшированную миниатюру", { url: video.url });
      return this.frameCache.get(video.url)!;
    }

    // Проверяем, не пытались ли мы уже извлечь кадр
    if (!this.extractionAttempts.has(video.url)) {
      // Пытаемся извлечь первый кадр
      // debugService.info("Инициирую извлечение первого кадра", {
      //   url: video.url,
      // });
      this.extractionAttempts.set(video.url, true);

      // Выбираем метод извлечения кадра в зависимости от типа видео
      if (video.url.includes(".m3u8")) {
        this.extractHlsFrame(video);
      } else {
        this.extractRegularFrame(video);
      }
    }

    // Возвращаем стандартную заглушку
    return this.getDefaultThumbnail();
  }

  /**
   * Извлекает первый кадр из HLS-видео с использованием HLS.js
   * @param video Видео для обработки
   */
  private static extractHlsFrame(video: Video | HlsVideo): void {
    // debugService.info("Начало извлечения первого кадра из HLS видео", {
    //   url: video.url,
    // });

    // Проверка поддержки HLS.js
    if (!Hls.isSupported()) {
      // debugService.error("HLS.js не поддерживается в этом браузере", {
      //   url: video.url,
      // });
      return;
    }

    // Создаем элемент видео
    const videoEl = document.createElement("video");
    videoEl.crossOrigin = "anonymous";
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.style.display = "none";
    document.body.appendChild(videoEl);

    // Таймаут для извлечения кадра
    const timeout = setTimeout(() => {
      // debugService.warn("Таймаут извлечения кадра HLS", { url: video.url });
      cleanup();
    }, 15000);

    // Функция очистки ресурсов
    const cleanup = () => {
      try {
        if (this.hlsInstance) {
          this.hlsInstance.destroy();
          this.hlsInstance = null;
        }

        videoEl.pause();
        videoEl.src = "";
        videoEl.load();

        if (videoEl.parentNode) {
          videoEl.parentNode.removeChild(videoEl);
        }

        clearTimeout(timeout);
      } catch (e) {
        console.log("Ошибка при очистке HLS ресурсов", { error: e });
        // debugService.error("Ошибка при очистке HLS ресурсов", { error: e });
      }
    };

    try {
      // Создаем HLS инстанс
      const hls = new Hls({
        enableWorker: false,
        lowLatencyMode: false,
        maxBufferLength: 5,
        startLevel: 0, // Самое низкое качество для быстрой загрузки
        capLevelToPlayerSize: true,
      });

      this.hlsInstance = hls;

      // Обработчики HLS событий
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        // debugService.info("HLS: Медиа элемент прикреплен", { url: video.url });
        hls.loadSource(video.url);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // debugService.info("HLS: Манифест разобран", {
        //   levels: data.levels.length,
        //   url: video.url,
        // });

        // Пытаемся проиграть и поставить на паузу
        videoEl
          .play()
          .then(() => {
            // После первого кадра ставим на паузу и делаем снимок
            setTimeout(() => {
              videoEl.pause();
              captureFrame();
            }, 500);
          })
          .catch((e) => {
            console.log("Не удалось проиграть HLS видео", { error: e });
            // debugService.error("Не удалось проиграть HLS видео", { error: e });
          });
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.log("HLS ошибка", {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
        });
        // debugService.error("HLS ошибка", {
        //   type: data.type,
        //   details: data.details,
        //   fatal: data.fatal,
        // });

        if (data.fatal) {
          cleanup();
        }
      });

      // Захват кадра
      const captureFrame = () => {
        try {
          if (videoEl.videoWidth === 0 || videoEl.videoHeight === 0) {
            // debugService.warn(
            //   "Размер видео еще не доступен, повторная попытка...",
            //   { url: video.url }
            // );
            setTimeout(captureFrame, 200);
            return;
          }

          // Создаем канвас для рисования кадра
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            throw new Error("Не удалось получить контекст canvas");
          }

          // Устанавливаем размеры canvas
          canvas.width = videoEl.videoWidth;
          canvas.height = videoEl.videoHeight;

          // Рисуем текущий кадр
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

          // Получаем URL данных
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

          // Проверяем на черный кадр
          if (this.isBlankFrame(ctx, canvas.width, canvas.height)) {
            // debugService.warn(
            //   "Получен пустой кадр, попытка получить следующий",
            //   { url: video.url }
            // );
            videoEl.currentTime += 1.0;
            setTimeout(captureFrame, 100);
            return;
          }

          // Сохраняем в кэш
          this.frameCache.set(video.url, dataUrl);

          // debugService.info("Успешно извлечен кадр из HLS видео", {
          //   url: video.url,
          //   width: canvas.width,
          //   height: canvas.height,
          // });

          // Вызываем событие для обновления UI
          this.notifyThumbnailReady(video.url);

          // Очищаем ресурсы
          cleanup();
        } catch (e) {
          console.log("Ошибка при захвате кадра из HLS", { error: e });
          // debugService.error("Ошибка при захвате кадра из HLS", { error: e });
          cleanup();
        }
      };

      // Прикрепляем HLS к элементу видео
      hls.attachMedia(videoEl);
    } catch (e) {
      console.log("Ошибка при инициализации HLS", { error: e });
      // debugService.error("Ошибка при инициализации HLS", { error: e });
      cleanup();
    }
  }

  /**
   * Извлекает первый кадр из обычного видео
   * @param video Видео для обработки
   */
  private static extractRegularFrame(video: Video | HlsVideo): void {
    // debugService.info("Начало извлечения первого кадра из обычного видео", {
    //   url: video.url,
    // });

    // Создаем видеоэлемент для извлечения кадра
    const videoEl = document.createElement("video");
    videoEl.crossOrigin = "anonymous";
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.style.display = "none";
    document.body.appendChild(videoEl);

    // Таймаут для извлечения кадра
    const timeout = setTimeout(() => {
      // debugService.warn("Таймаут извлечения кадра", { url: video.url });
      cleanup();
    }, 10000);

    // Функция очистки ресурсов
    const cleanup = () => {
      try {
        videoEl.pause();
        videoEl.src = "";
        videoEl.load();

        if (videoEl.parentNode) {
          videoEl.parentNode.removeChild(videoEl);
        }

        clearTimeout(timeout);
      } catch (e) {
        console.log("Ошибка при очистке", { error: e });
        // debugService.error("Ошибка при очистке", { error: e });
      }
    };

    // Обработчик события загрузки метаданных
    videoEl.onloadedmetadata = () => {
      // debugService.info("Метаданные видео загружены", {
      //   duration: videoEl.duration,
      //   videoWidth: videoEl.videoWidth,
      //   videoHeight: videoEl.videoHeight,
      // });

      // Устанавливаем начальное время для пропуска заставки
      videoEl.currentTime = 1.0;
    };

    // Обработчик события установки времени
    videoEl.onseeked = () => {
      // debugService.info("Видео перемотано на указанное время", {
      //   currentTime: videoEl.currentTime,
      // });
      captureFrame();
    };

    // Обработчик ошибок видео
    videoEl.onerror = () => {
      // console.log("Ошибка загрузки видео", {
      //   error,
      //   code: videoEl.error ? videoEl.error.code : "unknown",
      //   message: videoEl.error ? videoEl.error.message : "unknown",
      // });
      // debugService.error("Ошибка загрузки видео", {
      //   error,
      //   code: videoEl.error ? videoEl.error.code : "unknown",
      //   message: videoEl.error ? videoEl.error.message : "unknown",
      // });
      cleanup();
    };

    // Захват кадра
    const captureFrame = () => {
      try {
        // Проверяем готовность видео
        if (
          videoEl.readyState < 2 ||
          videoEl.videoWidth === 0 ||
          videoEl.videoHeight === 0
        ) {
          // debugService.warn(
          //   "Видео еще не готово для захвата кадра, повторная попытка...",
          //   {
          //     readyState: videoEl.readyState,
          //     videoWidth: videoEl.videoWidth,
          //     videoHeight: videoEl.videoHeight,
          //   }
          // );

          // Повторная попытка через короткий интервал
          setTimeout(captureFrame, 100);
          return;
        }

        // Создаем канвас для рисования кадра
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Не удалось получить контекст canvas");
        }

        // Устанавливаем размеры canvas
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;

        // Рисуем текущий кадр
        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

        // Получаем URL данных
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

        // Проверяем на черный кадр
        if (this.isBlankFrame(ctx, canvas.width, canvas.height)) {
          // debugService.warn("Получен пустой кадр, попытка получить следующий", {
          //   currentTime: videoEl.currentTime,
          // });

          // Перематываем еще вперед
          videoEl.currentTime += 1.0;
          return; // Выходим - onseeked вызовет captureFrame снова
        }

        // Сохраняем в кэш
        this.frameCache.set(video.url, dataUrl);

        // debugService.info("Успешно извлечен первый кадр", {
        //   url: video.url,
        //   width: canvas.width,
        //   height: canvas.height,
        // });

        // Вызываем событие для обновления UI
        this.notifyThumbnailReady(video.url);

        // Очищаем ресурсы
        cleanup();
      } catch (error) {
        console.log("Ошибка при извлечении кадра", {
          error,
          url: video.url,
        });
        // debugService.error("Ошибка при извлечении кадра", {
        //   error,
        //   url: video.url,
        // });
        cleanup();
      }
    };

    // Загружаем видео
    videoEl.src = video.url;
    videoEl.load();
  }

  /**
   * Проверяет, является ли кадр пустым (черным)
   * @param ctx Контекст канваса
   * @param width Ширина кадра
   * @param height Высота кадра
   * @returns true, если кадр пустой
   */
  private static isBlankFrame(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): boolean {
    const data = ctx.getImageData(0, 0, width, height).data;

    // Проверяем небольшое количество пикселей для оптимизации
    const sampleSize = Math.min(100, width * height);
    const step = Math.floor(data.length / 4 / sampleSize);

    let totalBrightness = 0;

    for (let i = 0; i < data.length; i += step * 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // Вычисляем яркость пикселя (0-255)
      const brightness = r * 0.299 + g * 0.587 + b * 0.114;
      totalBrightness += brightness;
    }

    // Среднее значение яркости
    const avgBrightness = totalBrightness / (data.length / step / 4);

    // Считаем изображение пустым, если средняя яркость < 20 (очень темное)
    return avgBrightness < 20;
  }

  /**
   * Уведомляет о готовности миниатюры
   */
  private static notifyThumbnailReady(videoUrl: string): void {
    // debugService.info("Отправка события thumbnail-ready", { url: videoUrl });
    // Создаем и вызываем событие
    const event = new CustomEvent("thumbnail-ready", {
      detail: { videoUrl },
    });
    window.dispatchEvent(event);
  }

  /**
   * Возвращает URL-адрес стандартной миниатюры видео
   * @returns URL стандартной миниатюры
   */
  static getDefaultThumbnail(): string {
    // Возвращаем SVG с иконкой плеера, закодированная в base64
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMwNzFhMzMiIG9wYWNpdHk9IjAuOCIvPgogIDxwYXRoIGQ9Ik00MCAzMEw3MCA1MEw0MCA3MFYzMFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=";
  }

  /**
   * Создает миниатюру для HLS-видео из уровней качества
   * @param video HLS видео
   * @returns URL миниатюры для HLS
   */
  static getHlsThumbnail(video: HlsVideo): string {
    // Используем стандартные поля
    return this.getVideoThumbnailUrl(video);
  }

  /**
   * Проверяет наличие миниатюры у видео
   * @param video Объект видео
   * @returns true, если миниатюра есть
   */
  static hasThumbnail(video: Video | HlsVideo): boolean {
    return !!(
      video.thumbnailUrl ||
      video.preview_url ||
      video.short_video_preview_url
    );
  }

  /**
   * Подписывается на обновления миниатюр
   * @param callback Функция обратного вызова при готовности миниатюры
   * @returns Функция для отписки
   */
  static subscribeThumbnailUpdates(
    callback: (videoUrl: string) => void
  ): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      callback(customEvent.detail.videoUrl);
    };

    window.addEventListener("thumbnail-ready", handler);

    // Возвращаем функцию для отписки
    return () => window.removeEventListener("thumbnail-ready", handler);
  }

  /**
   * Сбрасывает кэш миниатюр
   */
  static clearCache(): void {
    this.frameCache.clear();
    this.extractionAttempts.clear();

    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
  }
}

export default ThumbnailGenerator;
